import type { SalesEmployee, SalesMonthReport, SalesPeriod } from './types';
import { isSalesSystemAccount } from '@/features/Auth/lib/salesAccess';

type Manager = {
  is_admin?: number;
  bx?: { bx_user?: { bx_id: number }; department?: { id: number; role: string }[] };
};

export function canManageDepartment(user: Manager | null, departmentId: number): boolean {
  return !!user && (!!user.is_admin || isSalesSystemAccount(user) || Number(user.bx?.bx_user?.bx_id) === 8 ||
    !!user.bx?.department?.some(d => Number(d.id) === departmentId && d.role === 'head'));
}

export function todayInMoscow(now = new Date()): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Moscow' }).format(now);
}

export function monthEnd(year: number, month: number): string {
  return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
}

export function initialPeriod(departmentId: number, start: string | null, end: string | null): SalesPeriod {
  const date = start && /^\d{4}-\d{2}-\d{2}$/.test(start) ? start : todayInMoscow();
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  if (date.endsWith('-01') && month % 3 === 1 && end === monthEnd(year, month + 2)) {
    return { departmentId, year, period: 'quarter', quarter: Math.ceil(month / 3) };
  }
  return { departmentId, year, period: 'month', month };
}

export function previousDay(date: string): string {
  const day = new Date(`${date}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() - 1);
  return day.toISOString().slice(0, 10);
}

export const formatDate = (date: string) => date.slice(0, 10).split('-').reverse().join('.');
export const money = (value: number) => new Intl.NumberFormat('ru-RU', {
  style: 'currency', currency: 'RUB', maximumFractionDigits: 2,
}).format(value);

export function remainingPreview(source: SalesEmployee, date: string) {
  const closed = source.deals.filter(d => d.kind === 'closed' && d.date.slice(0, 10) < date);
  const remaining = (metric: 'opportunity' | 'profit') => {
    const fact = closed.reduce((sum, deal) => sum + Number(deal[metric]), 0);
    return source[metric].target - Math.min(source[metric].target, Math.max(0, fact));
  };
  return { opportunity: remaining('opportunity'), profit: remaining('profit') };
}

export function isEligibleRecipient(
  user: { id: number; active: number; uf_department: string },
  departmentId: number, sourceId: number, month: SalesMonthReport,
) {
  return !!Number(user.active) && user.id !== sourceId &&
    user.uf_department.split(',').map(Number).includes(departmentId) &&
    !month.transfers.some(t => t.fromUserId === user.id);
}

export const HISTORY_UPDATED = 'sales-plan-history-updated';
